import React, { useEffect, useState } from "react";
import { MPSharedTable, MPSharedDeleteModal, MPSharedHeader } from "../Shared";
import { useSearchQuery, usePagination, useDelete } from "../../../hooks";
import { useGetTripConfigurationsQuery, useDeleteEventConfigurationMutation } from "../services";
import { TripsForm } from "./form";
import { Link } from "react-router-dom";
import { getLocalizedString } from "../../../shared/translation";

export const TripsContainer = () => {
  const [idToDelete, setIdToDelete] = useState(null);
  const [idToEdit, setIdToEdit] = useState(null);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const { page, setPage, perPage, pagination } = usePagination();
  const [updated, setUpdated] = useState(false);
  const [deleted, setDeleted] = useState(false);

  const { setSimpleSearch, q } = useSearchQuery({
    page,
    setPage,
    simpleSearchKey: "name_cont",
  });

  const {
    data = {
      total_count: 0,
      data: [],
    },
    error,
    isFetching,
    isSuccess,
    refetch,
  } = useGetTripConfigurationsQuery({
    page,
    per_page: perPage,
    order_by: "created_at",
    order_dir: "desc",
    q,
  });

  useEffect(() => {
    if (updated || deleted) {
      refetch();
      setUpdated(false);
      setDeleted(false);
    }
  }, [updated, deleted]);

  const {
    deleteItem: deleteEventConfigurationPlan,
    deleteErrorMsg,
    setDeleteErrorMsg,
  } = useDelete({
    deleteMutation: useDeleteEventConfigurationMutation,
    closeModal: () => setShowDeleteWarning(false),
  });

  const headers = [
    {
      label: getLocalizedString("name", "Name"),
      key: "name",
      className: "name",
    },
    {
      label: getLocalizedString("type", "Type"),
      type: "component",
      className: "type",
      component: ({ data }) => {
        const name = data?.plan?.split("_").map((word) => {
          return word.charAt(0).toUpperCase() + word.substring(1, word.length) + " ";
        });

        return <span>{name}</span>;
      },
    },
    {
      label: getLocalizedString("device_models", "Device Models"),
      nestedValue: true,
      className: "device-model-count more text-center justify-center",
      getNestedValue: ({ device_models_count, id, name }) => {
        return (
          <>
            {device_models_count > 0 ? (
              <Link
                to={`/management_portal/devices/models?trip_event_configuration_id=${id}&name=${name}`}
                style={{ textDecoration: "none" }}
              >
                {device_models_count}
              </Link>
            ) : (
              <span>0</span>
            )}
          </>
        );
      },
    },
    {
      label: getLocalizedString("devices", "Devices"),
      nestedValue: true,
      className: "device-count more text-center justify-center",
      getNestedValue: ({ devices_count, id, name }) => {
        return (
          <>
            {devices_count > 0 ? (
              <Link
                to={`/management_portal/devices/list?trip_event_configuration_id=${id}&name=${name}`}
                style={{ textDecoration: "none" }}
              >
                {devices_count}
              </Link>
            ) : (
              <span>0</span>
            )}
          </>
        );
      },
    },
    {
      label: getLocalizedString("added_on", "Added On"),
      key: "created_at",
      className: "date",
    },
    // { label: "Added By", key: "created_by" },
  ];

  const onEdit = (data) => {
    setShowForm(true);
    setIdToEdit(data.id);
  };

  const onDelete = (data) => {
    setShowDeleteWarning(true);
    setIdToDelete(data.id);
  };

  const onCreate = () => {
    setShowForm(true);
    setIdToEdit(null);
  };

  const getDeleteItemName = (id) => {
    let name = "";
    const index = data?.data.findIndex((item) => item.id === id);

    if (index >= 0) {
      name = data.data[index].name;
    }
    return name;
  };

  return (
    <>
      <article className="main-container">
        <MPSharedHeader
          groupName="configurations"
          resourceName="Event Configurations"
          createNewHandler={onCreate}
          heading={getLocalizedString("configurations", "Configurations")}
          handleSearchKey={setSimpleSearch}
          simpleSearchPlaceholder={getLocalizedString("search_by_name", "Search by Name")}
        />

        <MPSharedTable
          resourceName="Event Configurations"
          data={data?.data || []}
          error={error}
          isLoading={isFetching}
          isSuccess={isSuccess}
          headers={headers}
          onEdit={onEdit}
          onDelete={onDelete}
          pagination={{ ...pagination, count: data.total_count }}
          skipClientId
          className="event-configuration"
          auditResource="EventConfiguration"
          auditKey="name"
        />
      </article>

      {showForm && (
        <TripsForm setUpdated={setUpdated} idToEdit={idToEdit} onClose={() => setShowForm(false)} />
      )}

      <MPSharedDeleteModal
        show={showDeleteWarning}
        entityName={getLocalizedString("trip_configuration", "Trip Configuration")}
        error={deleteErrorMsg}
        itemName={getDeleteItemName(idToDelete)}
        onHide={() => {
          setDeleteErrorMsg("");
          setShowDeleteWarning(false);
        }}
        onDelete={async () => {
          await deleteEventConfigurationPlan({ id: idToDelete });
          setDeleted(true);
        }}
      />
    </>
  );
};
